/**
 * The durable Hero: identity, allocation, equipment, and the combat identity a
 * run is built from.
 *
 * A class is no longer code here. `src/sim/arpg/classes/` holds the
 * declarations and this module reads the registry (PRD #792), so what remains
 * is Hero policy: what a valid Hero record is, what a fresh one starts as, and
 * what a run needs to know about one.
 */

import { equipment } from './content';
import type { EquippedItem } from './entity';
import { composeLoadout, type AbilityId } from './combat/abilityCatalog';
import {
  deriveCombatStats,
  type CombatStats,
} from './combat/derivedStats';
import {
  arpgClass,
  ARPG_CLASS_IDS,
  classBaseStats,
  isArpgClassId,
  isPlayableArpgClassId,
  isWornWeaponFamily,
  type ArpgClassId,
} from './arpg/classes';
import { attributePointsForLevel, HERO_LEVEL_CAP } from './arpg/progression';
import {
  ARPG_ITEM_SLOTS,
  arpgItemBaseOf,
  arpgItemContribution,
  arpgItemHue,
  arpgItemRequiredLevel,
  isArpgItemSlot,
  type ArpgItem,
  type ArpgItemSlot,
} from './arpgItem';
import type { EquipmentContribution } from './combat/derivedStats';

/** Every declared class id. A class that is not declared does not exist. */
export const HERO_CLASS_IDS: readonly ArpgClassId[] = ARPG_CLASS_IDS;
export type HeroClassId = ArpgClassId;

export interface HeroEquipment {
  /**
   * The weapon family the Hero holds, by its equipment id.
   *
   * It stays durable rather than being derived from the equipped weapon,
   * because a class whose implement is its hands holds a family and no item
   * (PRD #800), and because unequipping a weapon has to fall back to something.
   */
  mainHandId: string;
  /**
   * What is worn, by slot, as stash item ids (PRD #795).
   *
   * The single `mainHandItemId` this replaced was itemization's whole surface:
   * one slot, one item, no other slot expressible. A slot map is the shape the
   * ten declared slots need, and an absent slot is an empty one.
   */
  slots: Partial<Record<ArpgItemSlot, string>>;
  /**
   * The class's own look: the layers a Hero wears before any drop.
   *
   * Equipping a rolled item never rewrites this list. An item paints its layer
   * over these at identity time, dyed by its material tier, so taking an item
   * off restores the class look with nothing to undo.
   */
  equipped: EquippedItem[];
}

export interface HeroStats {
  strength: number;
  dexterity: number;
  intelligence: number;
  vitality: number;
  wisdom: number;
}

export interface Hero {
  classId: HeroClassId;
  level: number;
  xp: number;
  stats: HeroStats;
  equipment: HeroEquipment;
}

/** A zeroed allocation: what a Hero starts with and what a respec restores. */
export function noHeroStats(): HeroStats {
  return {
    strength: 0,
    dexterity: 0,
    intelligence: 0,
    vitality: 0,
    wisdom: 0,
  };
}

/** The five allocation components, for callers that walk them. */
export const HERO_STAT_KEYS: readonly (keyof HeroStats)[] = [
  'strength',
  'dexterity',
  'intelligence',
  'vitality',
  'wisdom',
];

/**
 * What a run needs to know about a Hero.
 *
 * The vitals and every other combat number are the derived record, not fields
 * of their own: `stats` is the fold's output and is the only thing a consumer
 * reads. The allocation that fed it stays as `allocated` for the UI and for
 * validation, and no combat code reads it.
 */
export interface HeroCombatIdentity {
  classId: HeroClassId;
  level: number;
  xp: number;
  /** The Hero's allocated points. Presentation and validation only. */
  allocated: HeroStats;
  /** The derived record every combat consumer reads. */
  stats: CombatStats;
  /** Catalog ability ids in loadout order. Every mechanic is read from the catalog. */
  abilities: AbilityId[];
  mainHand: {
    id: string;
    damage: number;
    grip: 'one-handed' | 'two-handed';
  };
  equipment: EquippedItem[];
  /** The items behind the derived record, by slot, for presentation and comparison. */
  items: Partial<Record<ArpgItemSlot, ArpgItem>>;
}

export function isHeroClassId(value: unknown): value is HeroClassId {
  return isArpgClassId(value);
}

export function isPlayableHeroClassId(value: unknown): value is HeroClassId {
  return isPlayableArpgClassId(value);
}

export function createHero(classId: unknown): Hero | null {
  const definition = isPlayableHeroClassId(classId) ? arpgClass(classId) : null;
  if (!definition) return null;
  const starting = definition.startingEquipment;
  return {
    classId: definition.id,
    level: 1,
    xp: 0,
    stats: noHeroStats(),
    equipment: {
      mainHandId: starting.mainHandId,
      slots: {},
      equipped: starting.equipped.map((item) => ({ ...item })),
    },
  };
}

/**
 * Whether an allocation is one this Hero could have earned.
 *
 * The sum-to-zero rule this replaced was the marker of a durable shape that had
 * outrun its mechanics: the record existed and nothing was allowed to be in it.
 * The rule now is the real one. Every component is a non-negative safe integer,
 * and the total spent is at most the points the Hero's level has granted.
 *
 * Spending less than everything is valid, and deliberately so: unspent points
 * are what a level-up hands the player, and a respec leaves every point
 * unspent until the player decides again.
 */
export function hasValidStats(stats: HeroStats, level: number): boolean {
  if (stats === null || typeof stats !== 'object') return false;
  let spent = 0;
  for (const key of HERO_STAT_KEYS) {
    const value = stats[key];
    if (!Number.isSafeInteger(value) || value < 0) return false;
    spent += value;
  }
  return spent <= attributePointsForLevel(level);
}

/** Points this Hero has earned and not yet spent. */
export function unspentAttributePoints(hero: Hero): number {
  const granted = attributePointsForLevel(hero?.level ?? 1);
  let spent = 0;
  for (const key of HERO_STAT_KEYS) {
    const value = hero?.stats?.[key];
    if (Number.isSafeInteger(value) && value > 0) spent += value;
  }
  return Math.max(0, granted - spent);
}

export function heroCombatIdentity(
  hero: Hero,
  stash: readonly ArpgItem[] = [],
): HeroCombatIdentity | null {
  if (
    !hero ||
    typeof hero !== 'object' ||
    !isPlayableHeroClassId(hero.classId) ||
    !Number.isSafeInteger(hero.level) ||
    hero.level < 1 ||
    hero.level > HERO_LEVEL_CAP ||
    !Number.isSafeInteger(hero.xp) ||
    hero.xp < 0 ||
    !hasValidStats(hero.stats, hero.level) ||
    !hero.equipment ||
    typeof hero.equipment !== 'object' ||
    typeof hero.equipment.mainHandId !== 'string' ||
    hero.equipment.slots === null ||
    typeof hero.equipment.slots !== 'object' ||
    Array.isArray(hero.equipment.slots) ||
    !Array.isArray(hero.equipment.equipped)
  ) {
    return null;
  }
  const definition = arpgClass(hero.classId);
  if (!definition) return null;
  const occupiedLayers = new Set<string>();
  let mainHandCount = 0;
  for (const item of hero.equipment.equipped) {
    if (
      item === null ||
      typeof item !== 'object' ||
      typeof item.id !== 'string' ||
      typeof item.layer !== 'string' ||
      (item.hue !== undefined && (!Number.isSafeInteger(item.hue) || item.hue < 0)) ||
      !equipment.has(item.id) ||
      !definition.allowedEquipmentIds.has(item.id)
    ) {
      return null;
    }
    const equipmentDefinition = equipment.get(item.id);
    if (equipmentDefinition.layer !== item.layer || occupiedLayers.has(item.layer)) {
      return null;
    }
    occupiedLayers.add(item.layer);
    if (item.layer === 'weapon') {
      if (item.id !== hero.equipment.mainHandId) return null;
      mainHandCount += 1;
    }
  }
  const mainHand = definition.weapons[hero.equipment.mainHandId];
  const worn = wornItems(hero, stash);
  if (!worn) return null;
  // Composition is the catalog's pure function: it applies the level gate,
  // rejects unknown ids, and returns the loadout in authored order. A failed
  // composition yields no abilities and therefore no identity, which fails
  // closed on a mis-authored class rather than shipping a partial loadout.
  const composed = composeLoadout(definition.abilities, [], { level: hero.level });
  const abilities = composed.ok ? [...composed.loadout] : [];
  // An empty loadout is not a valid Hero. That rule stays here rather than in
  // the catalog on purpose: `composeLoadout` is content composition and an empty
  // result is a legitimate answer for a fully level-gated base, while "a Hero
  // with nothing to cast cannot enter a dungeon" is Hero validity policy.
  // A class that holds something wears exactly one weapon-layer item, and a
  // class whose implement is its hands wears none. The count is the family's
  // to declare (PRD #800), not this function's to assume.
  const expectedWeaponItems = isWornWeaponFamily(mainHand) ? 1 : 0;
  if (
    !mainHand ||
    mainHandCount !== expectedWeaponItems ||
    abilities.length === 0
  ) {
    return null;
  }
  // The family a Hero holds is either the one its equipped weapon belongs to or
  // the one its class starts with, and never a third thing. Without this rule a
  // tampered record could name the class's best weapon family and collect its
  // damage band while owning nothing at all.
  const heldWeaponBase = worn.slots.mainHand
    ? arpgItemBaseOf(worn.slots.mainHand)
    : null;
  const expectedFamilyId =
    heldWeaponBase?.equipmentId ?? definition.startingEquipment.mainHandId;
  if (hero.equipment.mainHandId !== expectedFamilyId) return null;
  // A two-handed weapon excludes an off-hand. The grip is the class family's to
  // declare, so a two-handed bow needs no per-item flag to exclude a quiver.
  if (mainHand?.grip === 'two-handed' && worn.slots.offHand) return null;
  const contributions: EquipmentContribution[] = Object.values(worn.slots)
    .filter((item): item is ArpgItem => item !== undefined)
    .map((item) => arpgItemContribution(item));
  // A held weapon replaces the class's damage band rather than adding to it:
  // the item's implicit, already multiplied by its material tier, is what the
  // fold reads. A class with nothing in that slot keeps its declared band,
  // which is how the Mage channels bare-handed (PRD #800).
  const heldWeapon = worn.slots.mainHand !== undefined;
  const stats = deriveCombatStats(
    {
      level: hero.level,
      mainHand: { damage: heldWeapon ? 0 : mainHand.damage, grip: mainHand.grip },
      attributes: hero.stats,
      base: classBaseStats(definition, hero.level),
    },
    contributions,
  );
  return {
    classId: hero.classId,
    level: hero.level,
    xp: hero.xp,
    allocated: { ...hero.stats },
    // The one derivation: class base curve at this level, the allocation, and
    // then every equipped item's contribution through the fold's own equipment
    // stage. Itemization adds content and a roller, not a second pipeline.
    stats,
    abilities,
    mainHand: {
      id: hero.equipment.mainHandId,
      damage: stats.mainHandDamage,
      grip: mainHand.grip,
    },
    equipment: heroRenderLayers(hero, worn.slots),
    items: worn.slots,
  };
}

/**
 * Put one item on, as a pure transition.
 *
 * The rule that makes this small: a durable record holds what is worn, and
 * everything else about it is derived. So equipping is writing an item id into
 * a slot, plus the one thing that is not derived, which is the weapon family
 * the Hero holds. Whether the result is *legal* is not asked here: the caller
 * hands it to {@link heroCombatIdentity} against the account's stash, which is
 * the one gate, so there is no second copy of the rules to drift.
 */
export function equipHeroItem(hero: Hero, item: ArpgItem): Hero | null {
  const base = arpgItemBaseOf(item);
  if (!base) return null;
  const next = structuredClone(hero);
  // An item can only be in one slot, so putting it on takes it off wherever it
  // was. Without this, a client could equip a ring into both ring slots the
  // day a second one is declared.
  for (const slot of ARPG_ITEM_SLOTS) {
    if (next.equipment.slots[slot] === item.id) delete next.equipment.slots[slot];
  }
  next.equipment.slots[base.slot] = item.id;
  if (base.slot === 'mainHand' && base.equipmentId !== undefined) {
    next.equipment.mainHandId = base.equipmentId;
    next.equipment.equipped = [
      ...next.equipment.equipped.filter((worn) => worn.layer !== 'weapon'),
      { id: base.equipmentId, layer: 'weapon' },
    ];
  }
  // A two-handed weapon excludes an off-hand, so taking one up puts the
  // off-hand down rather than producing a record the gate would reject.
  const family = arpgClass(hero.classId)?.weapons[next.equipment.mainHandId];
  if (family?.grip === 'two-handed') delete next.equipment.slots.offHand;
  return next;
}

/**
 * Take one slot's item off.
 *
 * The main hand falls back to the class's declared starting family rather than
 * to nothing, because a Hero with no weapon family has no damage band at all
 * and would fail its own validity gate.
 */
export function unequipHeroSlot(hero: Hero, slot: ArpgItemSlot): Hero | null {
  const definition = arpgClass(hero.classId);
  if (!definition) return null;
  if (hero.equipment.slots[slot] === undefined) return null;
  const next = structuredClone(hero);
  delete next.equipment.slots[slot];
  if (slot === 'mainHand') {
    const fallback = definition.startingEquipment;
    next.equipment.mainHandId = fallback.mainHandId;
    next.equipment.equipped = [
      ...next.equipment.equipped.filter((worn) => worn.layer !== 'weapon'),
      ...fallback.equipped.filter((worn) => worn.layer === 'weapon').map((worn) => ({
        ...worn,
      })),
    ];
  }
  return next;
}

/** One validated slot map, resolved from stash item ids to the items themselves. */
interface WornItems {
  slots: Partial<Record<ArpgItemSlot, ArpgItem>>;
}

/**
 * Resolve and validate everything the Hero is wearing.
 *
 * This is the server-authoritative equip gate (user story twenty-two), and it
 * is deliberately one function rather than a check at each command: a tampered
 * client cannot wear what it has not earned, whichever path put the record
 * there. An item must be in the account's stash, must belong in the slot that
 * claims it, must allow the class, must not be worn twice, and must be within
 * the Hero's level. **Stats never gate equipment** (user story eight): nothing
 * here reads an attribute, so no build can lock itself out of its own gear.
 */
function wornItems(hero: Hero, stash: readonly ArpgItem[]): WornItems | null {
  const definition = arpgClass(hero.classId);
  if (!definition) return null;
  const slots: Partial<Record<ArpgItemSlot, ArpgItem>> = {};
  const wornIds = new Set<string>();
  for (const [slot, itemId] of Object.entries(hero.equipment.slots ?? {})) {
    if (itemId === undefined) continue;
    if (!isArpgItemSlot(slot) || typeof itemId !== 'string') return null;
    // One item in one slot, and never the same item in two.
    if (wornIds.has(itemId)) return null;
    const matches = stash.filter((candidate) => candidate.id === itemId);
    if (matches.length !== 1) return null;
    const item = matches[0]!;
    const base = arpgItemBaseOf(item);
    if (
      !base ||
      base.slot !== slot ||
      (base.classIds.size > 0 && !base.classIds.has(hero.classId)) ||
      hero.level < arpgItemRequiredLevel(item)
    ) {
      return null;
    }
    // A base that paints a layer must paint one this class is allowed to wear,
    // which keeps the equip gate and the paperdoll agreeing on one allowlist.
    if (base.equipmentId !== undefined) {
      if (
        !equipment.has(base.equipmentId) ||
        !definition.allowedEquipmentIds.has(base.equipmentId) ||
        equipment.get(base.equipmentId).layer !== base.layer
      ) {
        return null;
      }
    }
    // A held weapon has to be a family the class declares, and it has to be the
    // family the Hero's record says it holds.
    if (slot === 'mainHand') {
      const family = definition.weapons[base.equipmentId ?? ''];
      if (!family || base.equipmentId !== hero.equipment.mainHandId) return null;
    }
    wornIds.add(itemId);
    slots[slot] = item;
  }
  return { slots };
}

/**
 * What the paperdoll draws: the class's own look with every equipped item
 * painted over it, dyed by its material tier.
 *
 * The ladder rides the per-layer partial hue filters britbank already has, so a
 * Valorite chest is the shipped plate art at the Valorite hue and costs no new
 * art (PRD #713). A slot whose base paints no layer, which today is every glove
 * and every piece of jewellery, contributes nothing here and equips anyway.
 */
function heroRenderLayers(
  hero: Hero,
  slots: Partial<Record<ArpgItemSlot, ArpgItem>>,
): EquippedItem[] {
  const layers = new Map<string, EquippedItem>();
  for (const item of hero.equipment.equipped) {
    layers.set(item.layer, { ...item });
  }
  for (const item of Object.values(slots)) {
    if (!item) continue;
    const base = arpgItemBaseOf(item);
    if (!base?.equipmentId || !base.layer) continue;
    const hue = arpgItemHue(item);
    layers.set(base.layer, {
      id: base.equipmentId,
      layer: base.layer,
      ...(hue > 0 ? { hue } : {}),
    });
  }
  return [...layers.values()];
}
